import React from 'react';
import { DateInput } from '@mantine/dates';

interface DateOfBirthInputProps {
  label: string;
  name: string;
  value: string; // The value will be in string format (DD/MM/YYYY)
  onChange: (value: string | null) => void;
  required?: boolean;
  description?: string;
  placeholder?: string;
}

const DateOfBirthInput: React.FC<DateOfBirthInputProps> = ({
  label,
  name,
  value,
  onChange,
  required = false,
  description,
  placeholder = 'DD-MM-YYYY',
}) => {
  // Convert string (DD/MM/YYYY) to Date object
  const formatDateToDateObject = (dateString: string): Date | null => {
    const [day, month, year] = dateString.split('-');
    if (day && month && year) {
      return new Date(`${year}-${month}-${day}`); // Convert to YYYY-MM-DD format for Date object
    }
    return null;
  };

  const handleDateChange = (newValue: Date | null) => {
    if (newValue) {
      // Format the date as DD-MM-YYYY
      const day = String(newValue.getDate()).padStart(2, '0'); // Ensure two digits for the day
      const month = String(newValue.getMonth() + 1).padStart(2, '0'); // Ensure two digits for the month
      const year = newValue.getFullYear();
      const formattedDate = `${day}-${month}-${year}`; // Format: DD-MM-YYYY

      onChange(formattedDate); // Pass the formatted date string to parent
    } else {
      onChange(null); // If cleared, set to null
    }
  };

  // Calculate min and max dates (100 years ago and 14 years ago)
  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 100); // 100 years ago

  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() - 14); // 14 years ago (upper age limit)

  // Custom rendering for day cells
  const renderDay = (date: Date) => {
    const isDisabled = date < minDate || date > maxDate; // Check if the date is within the valid range
    return (
      <div
        style={{
          backgroundColor: isDisabled ? '#f5f5f5' : '', // Light gray for disabled
          color: isDisabled ? '#ccc' : '', // Dim color for disabled dates
          pointerEvents: isDisabled ? 'none' : 'auto', // Prevent clicks on disabled dates
        }}
      >
        {date.getDate()}
      </div>
    );
  };

  return (
    <DateInput
      label={label}
      name={name}
      value={value ? formatDateToDateObject(value) : null}
      onChange={handleDateChange}
      required={required}
      description={description}
      placeholder={placeholder}
      valueFormat="DD-MM-YYYY"
      minDate={minDate}
      maxDate={maxDate}
      renderDay={renderDay}
      defaultDate={maxDate} // Ensures the calendar opens at minDate month
    />
  );
};

export default DateOfBirthInput;
